import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCallJson, ApiError, authEvents } from '../utils/api';
import { API_URL } from '../config';
import logger from '../utils/logger';

const AuthContext = createContext();

// Frozen empty object used as the permissions fallback when no user is
// signed in. Hoisted to module scope so the surrounding useMemo's identity
// is stable across renders (a fresh `{}` literal in the value object
// invalidated every consumer on every parent re-render).
const EMPTY_PERMISSIONS = Object.freeze({});

// Cross-tab logout signal. BroadcastChannel where available, falls back
// to the storage event below.
const AUTH_BROADCAST_NAME = 'fabtraque-auth';
let authBroadcast = null;
if (typeof BroadcastChannel !== 'undefined') {
  try { authBroadcast = new BroadcastChannel(AUTH_BROADCAST_NAME); } catch { authBroadcast = null; }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // navigate changes identity on every pathname change under BrowserRouter.
  // Hold it in a ref (synced during render — safe for event-time reads) so
  // hardLogout/verifyToken stay stable and the mount effect below does NOT
  // re-run (and re-POST /api/auth/verify) on every navigation.
  const navRef = useRef(navigate);
  navRef.current = navigate;

  // Local clear-and-redirect routine used by every "session no longer
  // valid" trigger (token verify failed, 401 from api.js, cross-tab
  // signal, explicit logout). Centralised so the behaviour stays
  // consistent regardless of which path fired.
  const hardLogout = useCallback(({ notifyOtherTabs = false } = {}) => {
    setIsAuthenticated(false);
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear the selected-tenant context so the next operator on a shared
    // workstation does not silently inherit the previous superadmin's company.
    localStorage.removeItem('selectedCompanyId');
    Object.keys(localStorage)
      .filter((k) => k === 'selectedCompanyName' || k.startsWith('selectedCompanyName:'))
      .forEach((k) => localStorage.removeItem(k));
    try { window.dispatchEvent(new Event('companySelectionChanged')); } catch { /* ignore */ }
    if (notifyOtherTabs && authBroadcast) {
      try { authBroadcast.postMessage({ type: 'logout' }); } catch { /* ignore */ }
    }
    if (window.location.pathname !== '/auth') {
      navRef.current('/auth');
    }
  }, []);

  const verifyToken = useCallback(async (tokenToVerify) => {
    try {
      const data = await apiCallJson('/api/auth/verify', { method: 'POST' });
      setIsAuthenticated(true);
      const currentToken = localStorage.getItem('token');
      setToken(currentToken || tokenToVerify);
      if (data.user) {
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
      } else {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }
      }
    } catch (error) {
      if (error instanceof ApiError) {
        // Explicit non-OK from the server: the token is no longer valid.
        // Clear state and bounce to /auth via the central handler.
        logger.warn('Token verification failed (server response)', { status: error.status });
        hardLogout();
      } else {
        // Thrown errors here are typically network blips, NOT logical auth
        // failures — keep the existing session alive. The next attempt to
        // hit a protected endpoint will either succeed (network back) or
        // trigger the 401 path in api.js, which signals 'unauthenticated'.
        logger.warn('Token verification threw (network blip?); keeping session', { message: error?.message });
      }
    } finally {
      setLoading(false);
    }
  }, [hardLogout]);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      verifyToken(savedToken);
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  // Subscribe to api.js's auth bus. When a 401/refresh-failed fires, the
  // wrapper already cleared localStorage; we just sync in-memory state
  // and navigate. Using an event bus (vs a window.location reset inside
  // api.js) keeps the react tree mounted so in-flight UI can react.
  useEffect(() => {
    if (!authEvents) return undefined;
    const handler = () => {
      logger.warn('authEvents: unauthenticated, hard logout');
      hardLogout();
    };
    authEvents.addEventListener('unauthenticated', handler);
    return () => authEvents.removeEventListener('unauthenticated', handler);
  }, [hardLogout]);

  // Cross-tab logout: when another tab logs out, drop our session too.
  // Prevents shared workstations from leaking the previous operator's
  // state to the next one.
  useEffect(() => {
    if (!authBroadcast) {
      // Fallback for browsers without BroadcastChannel: storage event
      // fires when another tab clears the token from localStorage.
      const storageHandler = (e) => {
        if (e.key === 'token' && e.newValue === null) {
          hardLogout();
        }
      };
      window.addEventListener('storage', storageHandler);
      return () => window.removeEventListener('storage', storageHandler);
    }
    const bcHandler = (e) => {
      if (e?.data?.type === 'logout') hardLogout();
    };
    authBroadcast.addEventListener('message', bcHandler);
    return () => authBroadcast.removeEventListener('message', bcHandler);
  }, [hardLogout]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        setIsAuthenticated(true);
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        logger.info('User logged in', { email, firstName: data.user.firstName, lastName: data.user.lastName, userId: data.user.id });
        return { success: true, user: data.user };
      }
      logger.warn('Login failed', { email, reason: data.message });
      return { success: false, message: data.message || 'Échec de la connexion' };
    } catch (error) {
      logger.error('Login error', error, { email });
      return { success: false, message: 'Erreur réseau. Veuillez réessayer.' };
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) await verifyToken(savedToken);
  }, [verifyToken]);

  const logout = useCallback(() => {
    hardLogout({ notifyOtherTabs: true });
  }, [hardLogout]);

  const isSuperadmin = user?.role === 'superadmin';
  const isCompanyAdmin = user?.role === 'company_admin';
  const isAdmin = isSuperadmin || isCompanyAdmin; // For backward compatibility

  // Reads `user` directly and updates identity with it. Reading a ref that is
  // synced in an effect meant the FIRST authenticated render (user set,
  // effect not yet run) saw a null user, so PrivateRoute redirected non-admins
  // to /home on a hard load; and the stable identity meant menus memoized on
  // hasPermission never recomputed after a permission change. `value` already
  // depends on `user`, so making this depend on `user` adds no extra churn.
  const hasPermission = useCallback((section, action) => {
    const u = user;
    if (!u) return false;
    if (u.role === 'superadmin') return true;
    if (u.role === 'company_admin') return true;
    if (!u.permissions) return false;
    // settings.manage grants production edit access for company users
    if (section === 'production' && action === 'edit' && u.permissions.settings?.manage === true && u.companyId != null) {
      return true;
    }
    // Mirror backend/middleware/permissions.js exactly: fall back to the legacy
    // `holoptics` grant whenever the gmao action is not explicitly true (not
    // only when the whole gmao object is absent), or the two drift and lock
    // users out of GMAO the API would authorize.
    let sectionPerms = u.permissions[section] || {};
    if (section === 'gmao' && sectionPerms[action] !== true && u.permissions['holoptics']) {
      sectionPerms = u.permissions['holoptics'] || {};
    }
    return sectionPerms && sectionPerms[action] === true;
  }, [user]);

  const value = useMemo(() => ({
    isAuthenticated,
    token,
    user,
    loading,
    login,
    logout,
    refreshUser,
    isAdmin,
    isSuperadmin,
    isCompanyAdmin,
    hasPermission,
    permissions: user?.permissions || EMPTY_PERMISSIONS,
  }), [isAuthenticated, token, user, loading, login, logout, refreshUser, isAdmin, isSuperadmin, isCompanyAdmin, hasPermission]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
